export type ValueProposition<
  ValueProps extends {},
  AttachedProps extends {}
> = ValueProps & AttachedProps;

const isValueProposition = <ValueProps extends {}, AttachedProps extends {}>(
  x: Proposition<ValueProps, AttachedProps>
): x is ValueProposition<ValueProps, AttachedProps> => {
  return !isBooleanProposition(x) && !isImplicationProposition(x);
};

export type BooleanProposition<
  ValueProps extends {},
  AttachedProps extends {}
> = AttachedProps & {
  type: "or" | "and";
  propositions: Proposition<ValueProps, AttachedProps>[];
};

const isBooleanProposition = <ValueProps extends {}, AttachedProps extends {}>(
  x: Proposition<ValueProps, AttachedProps>
): x is BooleanProposition<ValueProps, AttachedProps> => {
  return typeof x === "object" && "type" in x && "propositions" in x;
};

export type ImplicationProposition<
  ValueProps extends {},
  AttachedProps extends {}
> = AttachedProps & {
  cause: Proposition<ValueProps, AttachedProps>;
  effect: Proposition<ValueProps, AttachedProps>;
};

const isImplicationProposition = <
  ValueProps extends {},
  AttachedProps extends {}
>(
  x: Proposition<ValueProps, AttachedProps>
): x is ImplicationProposition<ValueProps, AttachedProps> => {
  return typeof x === "object" && "cause" in x && "effect" in x;
};

export type Proposition<ValueProps extends {}, AttachedProps extends {}> =
  | ValueProposition<ValueProps, AttachedProps>
  | BooleanProposition<ValueProps, AttachedProps>
  | ImplicationProposition<ValueProps, AttachedProps>;

export const Proposition = {
  isValueProposition,
  isBooleanProposition,
  isImplicationProposition,
  builder<ValueProps extends {}, AttachedProps extends {}>() {
    type LocalProposition = Proposition<ValueProps, AttachedProps>;

    return (props: AttachedProps) => {
      const or = (
        propositions: LocalProposition[]
      ): BooleanProposition<ValueProps, AttachedProps> => {
        return {
          ...props,
          type: "or",
          propositions,
        };
      };
      const and = (
        propositions: LocalProposition[]
      ): BooleanProposition<ValueProps, AttachedProps> => {
        return {
          ...props,
          type: "and",
          propositions,
        };
      };
      const cause = (cause: LocalProposition) => {
        return {
          effect(
            proposition: LocalProposition
          ): ImplicationProposition<ValueProps, AttachedProps> {
            return {
              ...props,
              cause,
              effect: proposition,
            };
          },
        };
      };
      return { or, and, cause };
    };
  },
  map<ValueProps extends {}, AttachedProps extends {}, B>(
    proposition: Proposition<ValueProps, AttachedProps>,
    fctCase: (proposition: ValueProposition<ValueProps, AttachedProps>) => B,
    boolCase: (proposition: BooleanProposition<ValueProps, AttachedProps>) => B,
    implicationCase: (
      proposition: ImplicationProposition<ValueProps, AttachedProps>
    ) => B
  ): B {
    if (isBooleanProposition(proposition)) {
      return boolCase(proposition);
    } else if (isImplicationProposition(proposition)) {
      return implicationCase(proposition);
    } else {
      return fctCase(proposition);
    }
  },
  withAttachedProps<
    ValueProps extends {},
    AttachedProps extends {},
    C extends AttachedProps
  >(
    proposition: Proposition<ValueProps, AttachedProps>,
    mapFct: (proposition: Proposition<ValueProps, AttachedProps>) => C
  ): Proposition<ValueProps, C> {
    if (isBooleanProposition(proposition)) {
      return {
        ...proposition,
        ...mapFct(proposition),
        propositions: proposition.propositions.map((subProp) =>
          this.withAttachedProps(subProp, mapFct)
        ),
      };
    } else if (isImplicationProposition(proposition)) {
      return {
        ...proposition,
        ...mapFct(proposition),
        cause: this.withAttachedProps(proposition.cause, mapFct),
        effect: this.withAttachedProps(proposition.effect, mapFct),
      };
    } else {
      return { ...proposition, ...mapFct(proposition) };
    }
  },
  reduceFind<ValueProps extends {}, AttachedProps extends {}, B>(
    proposition: Proposition<ValueProps, AttachedProps>,
    testFct: (
      proposition: Proposition<ValueProps, AttachedProps>
    ) => B | undefined
  ): B | undefined {
    const testResult = testFct(proposition);
    if (testResult) {
      return testResult;
    }
    return Proposition.map(
      proposition,
      () => undefined,
      ({ propositions }) => {
        return propositions.reduce<B | undefined>(
          (acc, item) => acc || Proposition.reduceFind(item, testFct),
          undefined
        );
      },
      ({ cause, effect }) => {
        return [cause, effect].reduce<B | undefined>(
          (acc, item) => acc || Proposition.reduceFind(item, testFct),
          undefined
        );
      }
    );
  },
};
